import { getDefaultHeaders } from "@/APIs/getDefaultHeaders";

export interface ScanSessionData {
  sessionId: string;
  date: string;
  score: number;
  movedCount: number;
  unassignedCount: number;
  totalSeated: number;
  totalBookings: number;
  strategy: string;
  applied: boolean;
}

export interface ScanHistorySummary {
  averageScore: number;
  totalScans: number;
  totalAdditionalGuests: number;
  averageUtilisationImprovement: number;
  historicalAverageScore: number;
}

const isScanHistorySummary = (value: unknown): value is ScanHistorySummary => {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.averageScore === "number" &&
    typeof obj.totalScans === "number" &&
    typeof obj.totalAdditionalGuests === "number" &&
    typeof obj.averageUtilisationImprovement === "number" &&
    typeof obj.historicalAverageScore === "number"
  );
};

const isSuccessResponse = (value: unknown): value is { success: boolean } => {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.success === "boolean";
};

export const postScanSession = async (
  venueId: string,
  sessionData: ScanSessionData,
  baseUrl: string,
  token: string
): Promise<{ success: boolean }> => {
  const encodedVenueId = encodeURIComponent(venueId);
  const response = await fetch(
    `${baseUrl}/venues/${encodedVenueId}/ai/scan-history`,
    {
      method: "POST",
      headers: getDefaultHeaders(token),
      body: JSON.stringify(sessionData)
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to record scan session: ${response.status}`);
  }

  const data = await response.json();
  const result = data.payload ?? data;
  if (!isSuccessResponse(result)) {
    throw new Error("Unexpected response shape from postScanSession");
  }
  return result;
};

const isValidDateParam = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(value);

export const fetchScanHistorySummary = async (
  venueId: string,
  from: string | undefined,
  to: string | undefined,
  baseUrl: string,
  token: string
): Promise<ScanHistorySummary> => {
  const params = new URLSearchParams();
  if (from && isValidDateParam(from)) params.set("from", from);
  if (to && isValidDateParam(to)) params.set("to", to);

  const encodedVenueId = encodeURIComponent(venueId);
  const queryString = params.toString();
  const suffix = queryString ? `?${queryString}` : "";
  const url = `${baseUrl}/venues/${encodedVenueId}/ai/scan-history-summary${suffix}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getDefaultHeaders(token)
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch scan history summary: ${response.status}`);
  }

  const data = await response.json();
  const result = data.payload ?? data;
  if (!isScanHistorySummary(result)) {
    throw new Error("Unexpected response shape from fetchScanHistorySummary");
  }
  return result;
};
